import { faCheck, faClose, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
// import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colorPalette, cssBody400, cssBody600Bold, NewButton } from "@helium10/re-ui-components";
import { memo, useMemo } from "react";
import styled from "styled-components";

import { useTranslation } from "@/i18n/useTranslation";
import { useUploadFilesStore, MAX_SINGLE_FILE_SIZE } from "@/components/UploadFiles/store";
import { useUploadHistoryStore } from "@/components/UploadHistory/store";
import { documentCenterRoute } from "@/core/links";
import { getUrlWithSearchParams } from "@helium10/re-core";
import { getNavigate } from "@/utils/navigateService";

const BULK_UPLOAD_TASK_PREFIX = "document-center-batch-";

export const BatchUploadBanner = memo(() => {
  const { t } = useTranslation();
  const tasks = useUploadFilesStore((s) => s.tasks);
  const clearBulkUploadTasks = useUploadFilesStore((s) => s.clearBulkUploadTasks);

  const { totalFiles, completedFiles, isUploading, shouldShow } = useMemo(() => {
    const bulkTasks = Object.entries(tasks).filter(([id]) =>
      id.startsWith(BULK_UPLOAD_TASK_PREFIX),
    );
    let total = 0;
    let completed = 0;
    let uploading = false;
    // uploaded / error 均走完成态绿色 Banner（与产品约定一致）
    let hasTerminalBulkTask = false;

    for (const [, task] of bulkTasks) {
      if (task.status === "uploading") {
        uploading = true;
      }
      if (task.status === "uploaded" || task.status === "error") {
        hasTerminalBulkTask = true;
      }
      // 仅统计实际上传的文件(≤10MB) - 其实就是在排除 excluded 的文件
      // blob 缺失时视为无效
      const validFiles = task.fileList.filter(
        (f) => (f.file?.blob?.size ?? Infinity) <= MAX_SINGLE_FILE_SIZE && f.status !== "excluded",
      );
      // 用户点击上传后 task 才离开 init；分母/分子仅统计已开始上传的任务，避免 init 阶段 total 提前变化
      if (task.status !== "init") {
        total += validFiles.length;
        for (const file of validFiles) {
          // loaded 代表该文件已经上传完成，error 代表该文件上传失败，excluded 代表该文件被排除(没有上传)不在统计范围
          if (file.status === "loaded" || file.status === "error") {
            completed += 1;
          }
        }
      }
    }

    return {
      totalFiles: total,
      completedFiles: completed,
      isUploading: uploading,
      // init 仅有文件时不展示；uploading 或 task 终态（uploaded/error）才展示
      shouldShow: uploading || hasTerminalBulkTask,
    };
  }, [tasks]);

  const handleViewDetail = () => {
    const navigate = getNavigate();
    navigate(getUrlWithSearchParams(documentCenterRoute, {}, { hasAccountId: true }));
    useUploadHistoryStore.getState().openDrawer();
    clearBulkUploadTasks();
  };

  const handleClose = () => {
    clearBulkUploadTasks();
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <BannerWrapper $isUploading={isUploading}>
      {isUploading ? (
        <UploadingWrapper>
          <TitleWrapper>
            <StyledIcon icon={faSpinnerThird} spin />
            <Title>
              {t("batchUploadBanner.uploading.title", {
                completed: completedFiles,
                total: totalFiles,
              })}
            </Title>
          </TitleWrapper>
          <Subtitle>{t("batchUploadBanner.uploading.subtitle")}</Subtitle>
        </UploadingWrapper>
      ) : (
        <CompletedWrapper>
          <Wrapper>
            <TitleWrapper>
              <StyledIcon icon={faCheck} />
              <Title>
                {t("batchUploadBanner.completed.title", {
                  completed: completedFiles,
                  total: totalFiles,
                })}
              </Title>
            </TitleWrapper>
            <Subtitle>{t("batchUploadBanner.completed.subtitle")}</Subtitle>
          </Wrapper>
          <Actions>
            <NewButton buttonType="primary" size="100" onClick={handleViewDetail}>
              {t("batchUploadBanner.completed.viewDetail")}
            </NewButton>
            <CloseIcon icon={faClose} onClick={handleClose} />
          </Actions>
        </CompletedWrapper>
      )}
    </BannerWrapper>
  );
});

const BannerWrapper = styled.div<{ $isUploading: boolean }>`
  position: relative;
  z-index: 6;
  width: 100%;
  padding: 12px 24px 12px 48px;
  background-color: ${({ theme, $isUploading }) =>
    $isUploading ? theme.systemColors.blue50 : colorPalette.green50};
`;
const UploadingWrapper = styled.div``;
const CompletedWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Wrapper = styled.div``;
const TitleWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 18px;
`;
const StyledIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.systemColors.blue600};
`;
const Title = styled.div`
  ${cssBody600Bold}
  font-weight: 800;
`;
const Subtitle = styled.div`
  ${cssBody400}
  font-weight: 400;
  color: ${({ theme }) => theme.systemColors.black};
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sp12};
`;
const CloseIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
`;
